import React, { useEffect, useRef } from "react";
import styles from "./Billetto.module.scss";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import setInViewClasses from "blocks/hooks/setInViewClasses";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";

const SCRIPT_SRC = "https://billetto.dk/widget.js";

let scriptPromise: Promise<void> | null = null;

function isBillettoScript(embedCode: string): boolean {
    return embedCode.includes(SCRIPT_SRC);
}

function extractBillettoWidget(embedCode: string): string {
    return embedCode.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "").trim();
}

function loadBillettoScriptOnce(): Promise<void> {
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
        if (existing) return resolve();

        const script = document.createElement("script");
        script.src = SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Billetto script failed to load"));

        document.body.appendChild(script);
    });

    return scriptPromise;
}

const BillettoEmbed = ({ content, settings }: any) => {
    const wrapperClasses = getWrapperClasses(settings);
    const wrapperStyles = useWrapperStyles(settings);
    const { inViewElementRef, inViewClasses } = setInViewClasses(styles);
    const { splitClasses, splitStyles } = useSplitBackground({ content, settings });

    const hostRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!content?.embedCode || !hostRef.current) return;

        if (!isBillettoScript(content.embedCode)) return;

        const widgetHtml = extractBillettoWidget(content.embedCode);
        hostRef.current.innerHTML = widgetHtml;

        loadBillettoScriptOnce().then(() => {
            const widgets = hostRef.current?.querySelectorAll("billetto-widget, billetto-organiser-widget");

            widgets?.forEach((el) => {
                el.replaceWith(el.cloneNode(true));
            });
        });
    }, [content?.embedCode]);

    return (
        <section
            className={combineClasses(styles.wrapper, inViewClasses, ...wrapperClasses)}
            style={{ ...wrapperStyles, ...splitStyles }}
            ref={inViewElementRef}
        >
            <div className={combineClasses(styles.container, ...splitClasses)}>
                <div className={styles.content}>
                    <div ref={hostRef} />
                </div>
            </div>

            {settings.backgroundMedia?.[0] && <BackgroundMedia {...settings.backgroundMedia[0]} />}
        </section>
    );
};

export default BillettoEmbed;
